/**
 * Stockists Map Section
 * Interactive map showing stockist locations using Leaflet.js and OpenStreetMap
 */

class StockistsMap {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.zoom = parseInt(container.dataset.zoom) || 10;
    this.centerLat = parseFloat(container.dataset.centerLat) || null;
    this.centerLng = parseFloat(container.dataset.centerLng) || null;
    this.markerColor = container.dataset.markerColor || '#e85d04';

    this.map = null;
    this.markers = [];
    this.clusterGroup = null;
    this.stockists = [];

    this.activeCountries = new Set();
    this.filterContainer = null;
    // Normalized alias (uppercased, punctuation stripped) → canonical country name.
    // Covers ISO alpha-2, common English spellings, and French spellings since
    // the shop's primary market is France.
    this.countryAliases = {
      // France
      'FR': 'France', 'FRA': 'France', 'FRANCE': 'France',
      // Italy
      'IT': 'Italy', 'ITA': 'Italy', 'ITALY': 'Italy', 'ITALIE': 'Italy',
      // United Kingdom
      'UK': 'United Kingdom', 'GB': 'United Kingdom', 'GBR': 'United Kingdom',
      'UNITED KINGDOM': 'United Kingdom', 'GREAT BRITAIN': 'United Kingdom',
      'ENGLAND': 'United Kingdom', 'SCOTLAND': 'United Kingdom',
      'WALES': 'United Kingdom', 'NORTHERN IRELAND': 'United Kingdom',
      'ROYAUME UNI': 'United Kingdom', 'ROYAUMEUNI': 'United Kingdom',
      // Germany
      'DE': 'Germany', 'DEU': 'Germany', 'GERMANY': 'Germany', 'ALLEMAGNE': 'Germany',
      // Spain
      'ES': 'Spain', 'ESP': 'Spain', 'SPAIN': 'Spain', 'ESPAGNE': 'Spain',
      // Belgium
      'BE': 'Belgium', 'BEL': 'Belgium', 'BELGIUM': 'Belgium', 'BELGIQUE': 'Belgium',
      // Netherlands
      'NL': 'Netherlands', 'NLD': 'Netherlands', 'NETHERLANDS': 'Netherlands',
      'HOLLAND': 'Netherlands', 'PAYS BAS': 'Netherlands', 'PAYSBAS': 'Netherlands',
      // Switzerland
      'CH': 'Switzerland', 'CHE': 'Switzerland', 'SWITZERLAND': 'Switzerland',
      'SUISSE': 'Switzerland',
      // Austria
      'AT': 'Austria', 'AUT': 'Austria', 'AUSTRIA': 'Austria', 'AUTRICHE': 'Austria',
      // Portugal
      'PT': 'Portugal', 'PRT': 'Portugal', 'PORTUGAL': 'Portugal',
      // Ireland
      'IE': 'Ireland', 'IRL': 'Ireland', 'IRELAND': 'Ireland',
      'IRLANDE': 'Ireland', 'EIRE': 'Ireland',
      // Sweden
      'SE': 'Sweden', 'SWE': 'Sweden', 'SWEDEN': 'Sweden', 'SUEDE': 'Sweden',
      // Denmark
      'DK': 'Denmark', 'DNK': 'Denmark', 'DENMARK': 'Denmark', 'DANEMARK': 'Denmark',
      // Norway
      'NO': 'Norway', 'NOR': 'Norway', 'NORWAY': 'Norway', 'NORVEGE': 'Norway',
      // Finland
      'FI': 'Finland', 'FIN': 'Finland', 'FINLAND': 'Finland', 'FINLANDE': 'Finland',
      // Poland
      'PL': 'Poland', 'POL': 'Poland', 'POLAND': 'Poland', 'POLOGNE': 'Poland',
      // Luxembourg
      'LU': 'Luxembourg', 'LUX': 'Luxembourg', 'LUXEMBOURG': 'Luxembourg',
      // Other EU
      'CZ': 'Czechia', 'CZECH REPUBLIC': 'Czechia', 'CZECHIA': 'Czechia',
      'GR': 'Greece', 'GREECE': 'Greece', 'GRECE': 'Greece',
      'HU': 'Hungary', 'HUNGARY': 'Hungary', 'HONGRIE': 'Hungary',
      'RO': 'Romania', 'ROMANIA': 'Romania', 'ROUMANIE': 'Romania',
      'BG': 'Bulgaria', 'BULGARIA': 'Bulgaria',
      'SK': 'Slovakia', 'SLOVAKIA': 'Slovakia', 'SLOVAQUIE': 'Slovakia',
      'SI': 'Slovenia', 'SLOVENIA': 'Slovenia', 'SLOVENIE': 'Slovenia',
      'HR': 'Croatia', 'CROATIA': 'Croatia', 'CROATIE': 'Croatia',
      'EE': 'Estonia', 'ESTONIA': 'Estonia', 'ESTONIE': 'Estonia',
      'LV': 'Latvia', 'LATVIA': 'Latvia', 'LETTONIE': 'Latvia',
      'LT': 'Lithuania', 'LITHUANIA': 'Lithuania', 'LITUANIE': 'Lithuania',
      'IS': 'Iceland', 'ICELAND': 'Iceland', 'ISLANDE': 'Iceland',
      // Americas
      'US': 'United States', 'USA': 'United States', 'U S A': 'United States',
      'UNITED STATES': 'United States',
      'UNITED STATES OF AMERICA': 'United States',
      'AMERICA': 'United States', 'ETATS UNIS': 'United States',
      'ETATSUNIS': 'United States',
      'CA': 'Canada', 'CAN': 'Canada', 'CANADA': 'Canada',
      'MX': 'Mexico', 'MEX': 'Mexico', 'MEXICO': 'Mexico', 'MEXIQUE': 'Mexico',
      'BR': 'Brazil', 'BRA': 'Brazil', 'BRAZIL': 'Brazil', 'BRESIL': 'Brazil',
      'AR': 'Argentina', 'ARG': 'Argentina', 'ARGENTINA': 'Argentina', 'ARGENTINE': 'Argentina',
      // APAC & rest
      'JP': 'Japan', 'JPN': 'Japan', 'JAPAN': 'Japan', 'JAPON': 'Japan',
      'KR': 'South Korea', 'KOR': 'South Korea', 'SOUTH KOREA': 'South Korea',
      'CN': 'China', 'CHN': 'China', 'CHINA': 'China', 'CHINE': 'China',
      'HK': 'Hong Kong', 'HONG KONG': 'Hong Kong',
      'TW': 'Taiwan', 'TWN': 'Taiwan', 'TAIWAN': 'Taiwan',
      'SG': 'Singapore', 'SGP': 'Singapore', 'SINGAPORE': 'Singapore', 'SINGAPOUR': 'Singapore',
      'IN': 'India', 'IND': 'India', 'INDIA': 'India', 'INDE': 'India',
      'IL': 'Israel', 'ISR': 'Israel', 'ISRAEL': 'Israel',
      'AE': 'United Arab Emirates', 'UAE': 'United Arab Emirates',
      'UNITED ARAB EMIRATES': 'United Arab Emirates',
      'ZA': 'South Africa', 'SOUTH AFRICA': 'South Africa', 'AFRIQUE DU SUD': 'South Africa',
      'TR': 'Turkey', 'TUR': 'Turkey', 'TURKEY': 'Turkey', 'TURQUIE': 'Turkey',
      'AU': 'Australia', 'AUS': 'Australia', 'AUSTRALIA': 'Australia', 'AUSTRALIE': 'Australia',
      'NZ': 'New Zealand', 'NZL': 'New Zealand', 'NEW ZEALAND': 'New Zealand',
    };

    this.init();
  }

  init() {
    // Load stockist data from JSON
    const dataScript = document.getElementById(`stockists-data-${this.sectionId}`);
    if (dataScript) {
      try {
        this.stockists = JSON.parse(dataScript.textContent);
        console.log('Stockists Map: Loaded stockists data:', this.stockists);
      } catch (e) {
        console.error('Stockists Map: Error parsing stockists data:', e);
        return;
      }
    }

    // Preserve each stockist's original Liquid-rendered index so we can pair
    // it with its matching list DOM node after sorting.
    this.stockists.forEach((s, i) => {
      s.originalIndex = i;
    });

    // Filter out stockists without valid coordinates
    const originalCount = this.stockists.length;
    this.stockists = this.stockists.filter(
      (s) => s.latitude && s.longitude && !isNaN(parseFloat(s.latitude)) && !isNaN(parseFloat(s.longitude))
    );
    console.log(`Stockists Map: ${this.stockists.length} of ${originalCount} stockists have valid coordinates`);

    if (this.stockists.length === 0) {
      this.showNoStockistsMessage();
      return;
    }

    // Sort by country (derived from the last non-empty line of the address)
    this.sortByCountry();
    this.reorderListDom();

    this.createMap();
    this.addMarkers();
    this.bindListEvents();
    this.buildCountryFilter();
  }

  /**
   * Try to recognize a country from a single address line. Strips accents and
   * punctuation, uppercases, then checks the whole line and trailing n-grams
   * against the alias table. Returns a canonical display name or null.
   */
  countryFromLine(line) {
    if (!line) return null;
    const stripped = line
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip accents
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, ' ') // punctuation → space
      .replace(/\s+/g, ' ')
      .trim();
    if (!stripped) return null;

    if (this.countryAliases[stripped]) return this.countryAliases[stripped];

    // Try progressively shorter trailing n-grams: last 4, 3, 2, 1 words.
    // Handles "... FRANCE" or cases where the country is at the end of a line.
    const words = stripped.split(' ');
    for (let n = Math.min(4, words.length); n >= 1; n--) {
      const candidate = words.slice(-n).join(' ');
      if (this.countryAliases[candidate]) return this.countryAliases[candidate];
    }
    return null;
  }

  /**
   * Derive the canonical country for a stockist by scanning its address from
   * the last line upward, returning the first line that matches a known
   * country alias. Falls back to 'Other' when nothing matches.
   */
  getCountryKey(stockist) {
    if (!stockist || !stockist.address) return 'Other';
    const lines = stockist.address.split('\n').map((l) => l.trim()).filter(Boolean);
    for (let i = lines.length - 1; i >= 0; i--) {
      const match = this.countryFromLine(lines[i]);
      if (match) return match;
    }
    return 'Other';
  }

  sortByCountry() {
    this.stockists.sort((a, b) => {
      const ca = this.getCountryKey(a);
      const cb = this.getCountryKey(b);
      // Keep 'Other' at the bottom of the list
      if (ca === 'Other' && cb !== 'Other') return 1;
      if (cb === 'Other' && ca !== 'Other') return -1;
      const byCountry = ca.localeCompare(cb);
      if (byCountry !== 0) return byCountry;
      // Stable tie-break by name
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  scrollListToCluster(cluster) {
    const wrapper = this.container.closest('.stockists-map__wrapper');
    const listContainer = wrapper ? wrapper.querySelector('.stockists-map__list') : null;
    if (!listContainer) return;

    const childIndexes = cluster
      .getAllChildMarkers()
      .map((m) => m.stockistIndex)
      .filter((i) => typeof i === 'number')
      .sort((a, b) => a - b);

    if (childIndexes.length === 0) return;

    // Clear any previous cluster highlight
    listContainer
      .querySelectorAll('.stockists-map__item--in-cluster')
      .forEach((el) => el.classList.remove('stockists-map__item--in-cluster'));

    // Mark every list item belonging to the clicked cluster
    childIndexes.forEach((idx) => {
      const item = listContainer.querySelector(`[data-stockist-index="${idx}"]`);
      if (item) item.classList.add('stockists-map__item--in-cluster');
    });

    // Only scroll when the list itself is an internal-scroll container
    // (list-left / list-right layouts). For list-below — and on mobile where
    // the list sits below the map in page flow — scrollIntoView would yank
    // the page past the map, which is jarring. The cluster highlight still
    // signals which items matter so users can find them when they scroll.
    const listIsScrollable =
      listContainer.scrollHeight > listContainer.clientHeight + 4;
    if (listIsScrollable) {
      const firstItem = listContainer.querySelector(
        `[data-stockist-index="${childIndexes[0]}"]`
      );
      if (firstItem) {
        firstItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    // Auto-clear the highlight after a few seconds
    clearTimeout(this._clusterHighlightTimer);
    this._clusterHighlightTimer = setTimeout(() => {
      listContainer
        .querySelectorAll('.stockists-map__item--in-cluster')
        .forEach((el) => el.classList.remove('stockists-map__item--in-cluster'));
    }, 3500);
  }

  reorderListDom() {
    const wrapper = this.container.closest('.stockists-map__wrapper');
    const listContainer = wrapper ? wrapper.querySelector('.stockists-map__list') : null;
    if (!listContainer) return;

    // Map each existing list item by its original (Liquid) index
    const itemsByOriginalIndex = {};
    listContainer.querySelectorAll('[data-stockist-index]').forEach((el) => {
      itemsByOriginalIndex[el.dataset.stockistIndex] = el;
    });

    // Re-append in sorted order and rewrite data-stockist-index so it matches
    // the new position in this.stockists (which is what the click/hover handlers use)
    this.stockists.forEach((stockist, newIndex) => {
      const el = itemsByOriginalIndex[stockist.originalIndex];
      if (!el) return;
      el.dataset.stockistIndex = newIndex;
      listContainer.appendChild(el);
    });
  }

  showNoStockistsMessage() {
    this.container.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100%; min-height: 300px; background: #f5f5f5; border-radius: 8px;">
        <p style="color: #666; font-size: 1.4rem;">Add stockist locations in the theme editor to display the map.</p>
      </div>
    `;
  }

  createMap() {
    // Determine center point
    let center;
    if (this.centerLat && this.centerLng) {
      center = [this.centerLat, this.centerLng];
    } else if (this.stockists.length === 1) {
      center = [parseFloat(this.stockists[0].latitude), parseFloat(this.stockists[0].longitude)];
    } else {
      // Calculate center from all stockists
      const lats = this.stockists.map((s) => parseFloat(s.latitude));
      const lngs = this.stockists.map((s) => parseFloat(s.longitude));
      center = [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2];
    }

    // Create map
    this.map = L.map(this.container, {
      center: center,
      zoom: this.zoom,
      scrollWheelZoom: false,
    });

    // Add CartoDB Voyager tiles (clean style with better contrast)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    }).addTo(this.map);

    // Enable scroll zoom only after clicking on map
    this.map.on('click', () => {
      this.map.scrollWheelZoom.enable();
    });

    this.map.on('mouseout', () => {
      this.map.scrollWheelZoom.disable();
    });
  }

  addMarkers() {
    const bounds = [];
    const markerColor = this.markerColor;

    // Build a cluster group that shows a numbered badge whenever markers overlap
    const hasCluster = typeof L.markerClusterGroup === 'function';
    this.clusterGroup = hasCluster
      ? L.markerClusterGroup({
          showCoverageOnHover: false,
          spiderfyOnMaxZoom: true,
          // We handle click manually below to guarantee the cluster actually breaks apart
          zoomToBoundsOnClick: false,
          maxClusterRadius: 50,
          iconCreateFunction: (cluster) => {
            const count = cluster.getChildCount();
            let sizeClass = 'small';
            if (count >= 100) sizeClass = 'large';
            else if (count >= 10) sizeClass = 'medium';

            return L.divIcon({
              html:
                '<div class="stockists-cluster stockists-cluster--' +
                sizeClass +
                '" style="--cluster-color: ' +
                markerColor +
                ';"><span>' +
                count +
                '</span></div>',
              className: 'stockists-cluster-icon',
              iconSize: L.point(40, 40),
            });
          },
        })
      : null;

    if (this.clusterGroup) {
      this.clusterGroup.on('clusterclick', (event) => {
        const cluster = event.layer;
        const bounds = cluster.getBounds();
        const currentZoom = this.map.getZoom();
        const maxZoom = this.map.getMaxZoom();

        // Prefer fitting the children's bounds, but guarantee we always move
        // in at least two zoom levels so overlapping markers actually separate.
        const fitZoom = this.map.getBoundsZoom(bounds, false, [50, 50]);
        const targetZoom = Math.min(maxZoom, Math.max(fitZoom, currentZoom + 2));

        this.map.flyTo(cluster.getLatLng(), targetZoom, {
          animate: true,
          duration: 0.5,
        });

        this.scrollListToCluster(cluster);
      });
    }

    this.stockists.forEach((stockist, index) => {
      const lat = parseFloat(stockist.latitude);
      const lng = parseFloat(stockist.longitude);

      if (isNaN(lat) || isNaN(lng)) {
        console.warn('Stockists Map: Invalid coordinates for', stockist.name, '- lat:', stockist.latitude, 'lng:', stockist.longitude);
        return;
      }

      // Use circle marker for reliability
      const marker = L.circleMarker([lat, lng], {
        radius: 12,
        fillColor: this.markerColor,
        color: '#ffffff',
        weight: 3,
        opacity: 1,
        fillOpacity: 1
      });

      // Create popup content
      let popupContent = `<h4>${this.escapeHtml(stockist.name)}</h4>`;
      if (stockist.address) {
        popupContent += `<p>${this.escapeHtml(stockist.address).replace(/\n/g, '<br>')}</p>`;
      }
      if (stockist.phone) {
        const phoneClean = stockist.phone.replace(/[\s\-\(\)]/g, '');
        popupContent += `<p><a href="tel:${phoneClean}">${this.escapeHtml(stockist.phone)}</a></p>`;
      }
      if (stockist.website) {
        popupContent += `<p><a href="${stockist.website}" target="_blank" rel="noopener">Visit website</a></p>`;
      }

      marker.bindPopup(popupContent);

      // Store marker reference with index, and keep a pointer from stockist → marker
      marker.stockistIndex = index;
      stockist.marker = marker;
      this.markers.push(marker);
      bounds.push([lat, lng]);

      if (this.clusterGroup) {
        this.clusterGroup.addLayer(marker);
      } else {
        marker.addTo(this.map);
      }

      // Open popup on click
      marker.on('click', () => {
        this.highlightListItem(index);
      });
    });

    if (this.clusterGroup) {
      this.map.addLayer(this.clusterGroup);
    }

    // Fit bounds for multiple markers, otherwise use configured zoom
    if (bounds.length > 1) {
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }
  }

  bindListEvents() {
    const listItems = document.querySelectorAll(`[data-stockist-index]`);

    listItems.forEach((item) => {
      const index = parseInt(item.dataset.stockistIndex);

      item.addEventListener('click', () => {
        this.focusMarker(index);
        this.highlightListItem(index);
      });

      item.addEventListener('mouseenter', () => {
        if (this.markers[index]) {
          this.markers[index].openPopup();
        }
      });

      item.addEventListener('mouseleave', () => {
        if (this.markers[index]) {
          this.markers[index].closePopup();
        }
      });
    });
  }

  focusMarker(index) {
    const marker = this.markers[index];
    if (!marker) return;

    // Skip if this marker is currently filtered out of the cluster group
    if (this.clusterGroup && !this.clusterGroup.hasLayer(marker)) return;

    const latLng = marker.getLatLng();
    this.map.setView(latLng, Math.max(this.zoom, 14), { animate: true });

    if (this.clusterGroup && typeof this.clusterGroup.zoomToShowLayer === 'function') {
      this.clusterGroup.zoomToShowLayer(marker, () => marker.openPopup());
    } else {
      marker.openPopup();
    }
  }

  highlightListItem(index) {
    // Remove active class from all items
    const listItems = document.querySelectorAll(`[data-stockist-index]`);
    listItems.forEach((item) => item.classList.remove('is-active'));

    // Add active class to selected item
    const activeItem = document.querySelector(`[data-stockist-index="${index}"]`);
    if (activeItem) {
      activeItem.classList.add('is-active');
      activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Build the country filter pill row. Skips the whole thing if there are
   * fewer than 2 distinct countries (filtering would be pointless).
   */
  buildCountryFilter() {
    const wrapper = this.container.closest('.stockists-map');
    const container = wrapper
      ? wrapper.querySelector(`[data-stockists-filters="${this.sectionId}"]`)
      : null;
    if (!container) return;

    // Tally counts per country key
    const counts = new Map();
    this.stockists.forEach((s) => {
      const key = this.getCountryKey(s);
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    if (counts.size < 2) return;

    // Sort alphabetically, with "Other" pinned to the end.
    const countries = Array.from(counts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => {
        if (a.key === 'Other') return 1;
        if (b.key === 'Other') return -1;
        return a.key.localeCompare(b.key);
      });

    container.innerHTML = '';
    this.filterContainer = container;

    // "All" pill — active whenever no country filter is set
    const allPill = this.createFilterPill({
      label: 'All',
      count: this.stockists.length,
      isActive: true,
      onClick: () => {
        this.activeCountries.clear();
        this.renderFilterActiveStates();
        this.applyFilter();
      },
    });
    allPill.dataset.filterAll = 'true';
    container.appendChild(allPill);

    // One pill per country
    countries.forEach(({ key, count }) => {
      const pill = this.createFilterPill({
        label: key,
        count,
        isActive: false,
        onClick: () => {
          if (this.activeCountries.has(key)) {
            this.activeCountries.delete(key);
          } else {
            this.activeCountries.add(key);
          }
          this.renderFilterActiveStates();
          this.applyFilter();
        },
      });
      pill.dataset.filterCountry = key;
      container.appendChild(pill);
    });

    container.removeAttribute('hidden');
    const group = container.closest('[data-stockists-filters-group]');
    if (group) {
      group.removeAttribute('hidden');
      this.wireFilterCollapse(group);
    }

    // Paint the initial status line ("All 36") so the collapsed mobile trigger
    // has something to show.
    this.updateFilterStatus();
  }

  /**
   * Wire up the collapsible trigger — clicking toggles the panel on every
   * viewport. The initial state is expanded on desktop, collapsed on mobile,
   * so people see the options on big screens but aren't overwhelmed on small.
   */
  wireFilterCollapse(group) {
    const trigger = group.querySelector('[data-stockists-filters-trigger]');
    if (!trigger) return;

    this.filtersGroup = group;
    this.filtersTrigger = trigger;
    this.statusEl = group.querySelector('[data-stockists-status]');

    // Collapsed by default on every viewport — users opt in to reveal the pills.
    group.classList.remove('is-expanded');
    trigger.setAttribute('aria-expanded', 'false');

    trigger.addEventListener('click', () => {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      group.classList.toggle('is-expanded', !isExpanded);
    });
  }

  /**
   * Render a concise human-readable summary of the current filter state,
   * shown as the trigger's right-hand chip on mobile.
   */
  updateFilterStatus() {
    if (!this.statusEl) return;
    const active = Array.from(this.activeCountries);
    let text;
    if (active.length === 0) {
      text = `All · ${this.stockists.length}`;
    } else if (active.length === 1) {
      const visible = this.stockists.filter((s) => this.getCountryKey(s) === active[0]).length;
      text = `${active[0]} · ${visible}`;
    } else {
      const visible = this.stockists.filter((s) => this.activeCountries.has(this.getCountryKey(s))).length;
      text = `${active.length} countries · ${visible}`;
    }
    this.statusEl.textContent = text;
  }

  createFilterPill({ label, count, isActive, onClick }) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'stockists-map__filter-pill';
    if (isActive) {
      btn.classList.add('stockists-map__filter-pill--active');
      btn.setAttribute('aria-pressed', 'true');
    } else {
      btn.setAttribute('aria-pressed', 'false');
    }
    btn.innerHTML =
      `<span class="stockists-map__filter-pill-label">${this.escapeHtml(label)}</span>` +
      `<span class="stockists-map__filter-pill-count">${count}</span>`;
    btn.addEventListener('click', onClick);
    return btn;
  }

  renderFilterActiveStates() {
    if (!this.filterContainer) return;
    const nothingActive = this.activeCountries.size === 0;

    this.filterContainer.querySelectorAll('.stockists-map__filter-pill').forEach((el) => {
      let active;
      if (el.dataset.filterAll) {
        active = nothingActive;
      } else {
        active = this.activeCountries.has(el.dataset.filterCountry);
      }
      el.classList.toggle('stockists-map__filter-pill--active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  applyFilter() {
    const showAll = this.activeCountries.size === 0;

    this.stockists.forEach((stockist, index) => {
      const key = this.getCountryKey(stockist);
      const visible = showAll || this.activeCountries.has(key);

      // Toggle list item visibility
      const item = document.querySelector(
        `[data-stockist-index="${index}"]`
      );
      if (item) item.hidden = !visible;

      // Toggle marker membership in the cluster group (or directly on the map)
      const marker = stockist.marker;
      if (!marker) return;

      if (this.clusterGroup) {
        const inGroup = this.clusterGroup.hasLayer(marker);
        if (visible && !inGroup) this.clusterGroup.addLayer(marker);
        else if (!visible && inGroup) this.clusterGroup.removeLayer(marker);
      } else if (this.map) {
        const onMap = this.map.hasLayer(marker);
        if (visible && !onMap) marker.addTo(this.map);
        else if (!visible && onMap) this.map.removeLayer(marker);
      }
    });

    this.refitVisibleBounds();
    this.updateFilterStatus();
  }

  refitVisibleBounds() {
    if (!this.map) return;

    const showAll = this.activeCountries.size === 0;
    const visiblePoints = [];
    this.stockists.forEach((s) => {
      const key = this.getCountryKey(s);
      if (showAll || this.activeCountries.has(key)) {
        const lat = parseFloat(s.latitude);
        const lng = parseFloat(s.longitude);
        if (!isNaN(lat) && !isNaN(lng)) visiblePoints.push([lat, lng]);
      }
    });

    if (visiblePoints.length === 0) return;
    if (visiblePoints.length === 1) {
      this.map.flyTo(visiblePoints[0], Math.max(this.zoom, 12), { duration: 0.6 });
      return;
    }
    this.map.flyToBounds(visiblePoints, { padding: [40, 40], duration: 0.6 });
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize all stockists maps when DOM is ready
function initStockistsMaps() {
  const maps = document.querySelectorAll('.stockists-map__map');
  maps.forEach((mapContainer) => {
    // Check if Leaflet is loaded
    if (typeof L !== 'undefined') {
      new StockistsMap(mapContainer);
    } else {
      // Wait for Leaflet to load
      const checkLeaflet = setInterval(() => {
        if (typeof L !== 'undefined') {
          clearInterval(checkLeaflet);
          new StockistsMap(mapContainer);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => clearInterval(checkLeaflet), 10000);
    }
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStockistsMaps);
} else {
  initStockistsMaps();
}

// Re-initialize on section load (Shopify theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const mapContainer = section.querySelector('.stockists-map__map');
  if (mapContainer && typeof L !== 'undefined') {
    new StockistsMap(mapContainer);
  }
});
